import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Input,
    OnInit,
    output,
    ViewEncapsulation,
    inject,
    signal,
    computed,
} from '@angular/core';
import { AppTextService } from '@fi-shared/app-text';
import { ISectionCardData } from '@fi-shared/fi-section-card/fi-section-card';
import { IChecklistSchedule, IChecklistTimeIntervalGroup } from '@fi-shared/checklist/checklist-v2';
import { FiChecklistTimeIntervalLabels } from '@fi-shared/checklist/schedule/fi-checklist-time-interval-type';
import { IChecklist2ScheduleEditTimeIntervalDialogData } from '@fi-shared/checklist/schedule/schedule-edit/schedule-time/time-intervals-dialog/time-intervals-dialog.component';
import { FiChecklist2ScheduleIntervalService } from '@fi-shared/checklist/schedule/services/fi-checklist2-schedule-interval.service';
import { ValidationErrors } from '@angular/forms';
import { FiComponentsContainer } from '@fi-shared/fi-components-container';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { FiChecklist2ScheduleDialogService } from '../../services/fi-checklist2-schedule-dialog.service';

@Component({
    selector: 'fi-checklist2-schedule-edit-schedule-time',
    templateUrl: './schedule-time.component.html',
    styleUrls: ['./schedule-time.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checklist2-schedule-edit-schedule-time' },
    providers: [{ provide: FiComponentsContainer, useExisting: FiChecklist2ScheduleEditScheduleTimeComponent }],
    standalone: false,
})
export class FiChecklist2ScheduleEditScheduleTimeComponent extends FiComponentsContainer implements OnInit {
    @Input({ required: true }) checklistSchedule: IChecklistSchedule;
    public checklistScheduleChange = output<IChecklistSchedule>();

    @Input({ required: true }) auditMode: boolean;
    @Input() scrollableElementRefs: ElementRef[];

    protected intervalGroupSignal = signal<IChecklistTimeIntervalGroup>({});
    private isIntervalDataMissing = computed(() =>
        this.fiChecklist2ScheduleIntervalService.isIntervalDataMissing(this.intervalGroupSignal()),
    );

    private appTextService = inject(AppTextService);
    private fiChecklist2ScheduleIntervalService = inject(FiChecklist2ScheduleIntervalService);
    private saveChangesWarningService = inject(SaveChangesWarningService);
    private fiChecklist2ScheduleDialogService = inject(FiChecklist2ScheduleDialogService);

    protected readonly timeScheduleContentCardData: Readonly<ISectionCardData> = {
        headerTextLabel: this.appTextService.get('checklist.time_schedule'),
        subheaderTextLabel: this.appTextService.get('checklist.schedule_time_description'),
        id: undefined,
    };

    constructor() {
        super();
    }

    public ngOnInit(): void {
        this.setIntervalGroups();
    }

    private setIntervalGroups(): void {
        this.intervalGroupSignal.set(
            this.fiChecklist2ScheduleIntervalService.groupTimeIntervals(
                this.checklistSchedule.TimeIntervals,
                this.checklistSchedule.TimeIntervalType,
            ),
        );
    }

    protected getChecklistScheduleTimeIntervalLabel(): string {
        return FiChecklistTimeIntervalLabels.find((interval) => interval.Id === this.checklistSchedule.TimeIntervalType)
            ?.Name;
    }

    protected isNotAuditInterval(): boolean {
        return !this.fiChecklist2ScheduleIntervalService.isAuditInterval(this.checklistSchedule.TimeIntervalType);
    }

    protected editChecklistScheduleTime(): void {
        this.fiChecklist2ScheduleDialogService
            .getEditChecklistScheduleTimeDialog(this.checklistSchedule, this.intervalGroupSignal(), this.auditMode)
            .onYesButtonClick((result: IChecklist2ScheduleEditTimeIntervalDialogData) => {
                this.checklistSchedule.ScheduleType = result.scheduleType;
                this.checklistSchedule.TimeIntervalType = result.timeIntervalType;
                this.intervalGroupSignal.set(
                    this.fiChecklist2ScheduleIntervalService.getSortedTimeIntervalGroup(
                        result.timeIntervalGroup,
                        result.timeIntervalType,
                    ),
                );
                this.checklistSchedule.TimeIntervals = this.fiChecklist2ScheduleIntervalService.getIntervalsFromGroup(
                    this.intervalGroupSignal(),
                );
                this.checklistSchedule.RemindTime = result.remindTime;
                this.checklistScheduleChange.emit(this.checklistSchedule);
            })
            .afterClosed(() => (this.saveChangesWarningService.closeDialogOnTransition = false));

        this.saveChangesWarningService.closeDialogOnTransition = true;
    }

    protected validateScheduleTimeFn = (): ValidationErrors => {
        return {
            'no-interval-data': this.isIntervalDataMissing(),
        };
        /*'no-remind-date': !this.fiChecklistHelperService.isOnAuditOrDemandChecklistSchedule(this.checklistSchedule.ScheduleType) &&
                !this.fiChecklist2ScheduleTimeIntervalHelperService.isRemindTimeSet(this.checklistSchedule.RemindTime)*/
    };
}
